'use client'

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  cloneElement,
  isValidElement,
} from 'react'
import { AdvertisingModal, AdvertisingModalProps } from './AdvertisingModal'
import { useExitIntent } from './useExitIntent'
import { getAdvertisingModalOpenState } from './advertisingModalState'
import { safeLocalStorage } from './utils/errorHandling'
import type { AdvertisingModalTriggerType } from './types'

export interface AdvertisingModalTriggerProps
  extends Omit<AdvertisingModalProps, 'isOpen' | 'onClose'> {
  trigger: AdvertisingModalTriggerType
  triggerDelay?: number
  exitIntentSensitivity?: number
  children?: React.ReactElement
  onTrigger?: () => void
  showOnce?: boolean
  cookieName?: string
  cookieExpireDays?: number
}

/**
 * Wrapper component that handles different trigger mechanisms for the AdvertisingModal
 * @param trigger Type of trigger: 'manual', 'exit-intent', 'timer', or 'click'
 * @param triggerDelay For 'timer' trigger: delay in milliseconds (default: 5000)
 * @param exitIntentSensitivity For 'exit-intent': pixels from top (default: 30)
 * @param children For 'click' trigger: element to click on
 * @param onTrigger Callback when modal is triggered
 * @param showOnce If true, modal only shows once per session (uses localStorage)
 * @param cookieName localStorage key for showOnce feature (default: 'advertisingModalShown')
 * @param cookieExpireDays Days until showOnce resets (default: 7)
 * @note For manual control without triggers, use the useAdvertisingModal hook instead
 */
export const AdvertisingModalTrigger = ({
  trigger,
  triggerDelay = 5000,
  exitIntentSensitivity = 30,
  children,
  onTrigger,
  showOnce = false,
  cookieName = 'advertisingModalShown',
  cookieExpireDays = 7,
  ...modalProps
}: AdvertisingModalTriggerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  //const hasTriggeredRef = useRef(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const originalOnClickRef = useRef<
    ((e: React.MouseEvent) => void) | undefined
  >(undefined)

  const handleTrigger = useCallback(() => {
    // For timer and exit-intent, only trigger once per session
    // For click, allow multiple triggers (unless showOnce is enabled)
    /*
    if (trigger !== 'click' && hasTriggeredRef.current) {
      return
    }*/

    // Check if another advertising modal is already open
    if (getAdvertisingModalOpenState()) {
      return
    }

    // Check if shown before
    if (showOnce) {
      const stored = safeLocalStorage.getItem(cookieName)
      if (stored) {
        try {
          const data = JSON.parse(stored)
          const expiryDate = new Date(data.expiry)
          const now = new Date()

          if (now <= expiryDate) {
            return
          } else {
            safeLocalStorage.removeItem(cookieName)
          }
        } catch {
          // Invalid stored data, remove it
          safeLocalStorage.removeItem(cookieName)
        }
      }
    }

    //hasTriggeredRef.current = true
    setIsOpen(true)

    // Mark as shown
    if (showOnce) {
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + cookieExpireDays)
      const data = JSON.stringify({
        shown: true,
        expiry: expiryDate.toISOString(),
      })
      safeLocalStorage.setItem(cookieName, data)
    }

    if (onTrigger) {
      onTrigger()
    }
  }, [onTrigger, showOnce, cookieName, cookieExpireDays])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  // Capture original onClick handler from children for click trigger
  useEffect(() => {
    if (trigger === 'click' && children && isValidElement(children)) {
      originalOnClickRef.current = (
        children.props as { onClick?: (e: React.MouseEvent) => void }
      )?.onClick
    }
  }, [trigger, children])

  // Memoized click handler for click trigger
  const handleClick = useCallback(() => {
    handleTrigger()
  }, [handleTrigger])

  // Timer trigger
  useEffect(() => {
    if (trigger === 'timer') {
      timerRef.current = setTimeout(() => {
        handleTrigger()
      }, triggerDelay)

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
        }
      }
    }
  }, [trigger, triggerDelay, handleTrigger])

  // Exit intent trigger
  useExitIntent({
    onExitIntent: handleTrigger,
    enabled: trigger === 'exit-intent',
    sensitivity: exitIntentSensitivity,
  })

  // Render based on trigger type
  const shouldRenderClickWrapper =
    trigger === 'click' && children && isValidElement(children)

  // For click trigger, wrap children with click handler

  if (shouldRenderClickWrapper) {
    return (
      <>
        {cloneElement(
          children as React.ReactElement<{
            onClick?: (e: React.MouseEvent) => void
          }>,
          {
            onClick: handleClick,
          }
        )}
        <AdvertisingModal
          {...modalProps}
          isOpen={isOpen}
          onClose={handleClose}
        />
      </>
    )
  }

  // For all other triggers (timer, exit-intent, manual), render modal directly
  return (
    <AdvertisingModal {...modalProps} isOpen={isOpen} onClose={handleClose} />
  )
}

/**
 * Hook to manually control the advertising modal
 * Useful when you need programmatic control over the modal
 *
 * @example
 * const { isOpen, open, close } = useAdvertisingModal()
 *
 * <button onClick={open}>Show Offer</button>
 * <AdvertisingModal
 *   isOpen={isOpen}
 *   onClose={close}
 *   content={...}
 *   onSubmit={...}
 * />
 */
export const useAdvertisingModal = () => {
  const [isOpen, setIsOpen] = useState(false)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(prev => !prev), [])

  return {
    isOpen,
    open,
    close,
    toggle,
  }
}
